/**
 * Checks if a word is a valid Roman numeral.
 * 
 * This function validates against common Roman numerals used in fund names,
 * avoiding false positives like single letters that could be part of other words.
 * 
 * Valid patterns include: I, II, III, IV, V, VI, VII, VIII, IX, X, XI, XII, etc.
 * Invalid patterns: single letters like 'v' in 'v20' or 'version' are rejected
 * when they don't form valid Roman numeral sequences.
 * 
 * @param word - The word to check
 * @returns true if the word is a valid Roman numeral, false otherwise
 */
const isRomanNumeral = (word: string): boolean => {
  if (!word) return false;
  
  // Convert to uppercase for comparison
  const upperWord = word.toUpperCase();
  
  // Roman numerals must:
  // 1. Only contain valid Roman numeral characters
  // 2. Follow valid Roman numeral patterns
  
  // First, check if it only contains Roman numeral characters
  if (!/^[IVXLCDM]+$/.test(upperWord)) {
    return false;
  }
  
  // Reject single letters that are unlikely to be Roman numerals in context
  // (e.g., 'V', 'X', 'I' alone could be abbreviations or variables)
  // Exception: Allow common single-letter Roman numerals in fund names (I, V, X)
  if (upperWord.length === 1) {
    // Only accept I, V, X as single-letter Roman numerals
    return ['I', 'V', 'X'].includes(upperWord);
  }
  
  // Validate proper Roman numeral structure
  // This regex matches valid Roman numeral patterns up to 399 (CCCXCIX)
  // which covers most common fund naming scenarios
  const romanNumeralPattern = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
  
  return romanNumeralPattern.test(upperWord);
};

export default isRomanNumeral;
